import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Checkbox,
  Divider,
  Menu,
  MenuItem,
  Collapse,
  FormControlLabel,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem as SelectMenuItem,
  FormControl,
  InputLabel,
  Grid,
  TextField,
} from '@mui/material';

import config from '../twh/config';
import { getFirstErrorMessage } from '../twh/functions';

import iconAngleDown from '../assets/icons/angle-down.svg';

export default function OpeningHours() {
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  // Time options: 00:00 to 23:30 every 30 minutes
  const generateTimeOptions = () => {
    const options = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 30) {
        const hour = h.toString().padStart(2, '0');
        const minute = m.toString().padStart(2, '0');
        options.push(`${hour}:${minute}`);
      }
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  // ── STATE ─────────────────────────────────────────────────────────────────
  const [standardHours, setStandardHours] = useState({});
  const [loadingStandard, setLoadingStandard] = useState(true);
  const [errorStandard, setErrorStandard] = useState(null);
  const [savingStandard, setSavingStandard] = useState(false);
  const [expandedDay, setExpandedDay] = useState(null);

  const [customHours, setCustomHours] = useState([]);
  const [loadingCustom, setLoadingCustom] = useState(true);
  const [errorCustom, setErrorCustom] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [formError, setFormError] = useState('');
  const [submittingCustom, setSubmittingCustom] = useState(false);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // ── FETCH FUNCTIONS ───────────────────────────────────────────────────────
  const fetchStandardHours = async () => {
    try {
      setLoadingStandard(true);
      setErrorStandard(null);

      const res = await fetch(
        `${config.reactApi_baseUrl}/react-api/opening-hours/standard-opening-hours.php`,
        { credentials: 'include' }
      );

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();

      if (data.status === true) {
        const hoursObj = {};
        (data.data || []).forEach((item) => {
          hoursObj[item.day] = {
            opensAt: item.opensAt || '00:00',
            closesAt: item.closesAt || '00:00',
            isClosed: item.isClosed || false,
          };
        });
        setStandardHours(hoursObj);
      } else {
        throw new Error(getFirstErrorMessage(data.errorMsg) || 'Failed to load');
      }
    } catch (err) {
      setErrorStandard(err.message || 'Failed to load standard hours');
    } finally {
      setLoadingStandard(false);
    }
  };

  const fetchCustomHours = async () => {
    try {
      setLoadingCustom(true);
      setErrorCustom(null);

      const res = await fetch(
        `${config.reactApi_baseUrl}/react-api/opening-hours/custom-opening-hours.php`,
        { credentials: 'include' }
      );

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();

      if (data.status === true) {
        setCustomHours(data.data || []);
      } else {
        throw new Error(getFirstErrorMessage(data.errorMsg) || 'Failed to load');
      }
    } catch (err) {
      setErrorCustom(err.message || 'Failed to load custom hours');
    } finally {
      setLoadingCustom(false);
    }
  };

  // ── STANDARD HANDLERS ─────────────────────────────────────────────────────
  const toggleExpanded = (day) => {
    setExpandedDay(expandedDay === day ? null : day);
  };

  const handleTimeChange = (day, field, value) => {
    setStandardHours((prev) => ({
      ...prev,
      [day]: { ...prev[day], [field]: value },
    }));
  };

  const handleClosedChange = (day, checked) => {
    setStandardHours((prev) => ({
      ...prev,
      [day]: { ...prev[day], isClosed: checked },
    }));
    if (checked && expandedDay === day) {
      setExpandedDay(null);
    }
  };

  const handleSaveStandard = async () => {
    setSavingStandard(true);
    try {
      const formData = new FormData();
      formData.append('issetAddEditOpeningHours', '1');

      days.forEach((day, i) => {
        const h = standardHours[day] || { opensAt: '00:00', closesAt: '00:00', isClosed: false };
        formData.append(`daysName[${i}]`, day);
        formData.append(`closed[${i}]`, h.isClosed ? 'Closed' : 'Opened');
        formData.append(`opensAt[${i}]`, h.opensAt);
        formData.append(`closesAt[${i}]`, h.closesAt);
      });

      const res = await fetch(
        `${config.reactApi_baseUrl}/react-api/opening-hours/update-standard-opening-hours.php`,
        { method: 'POST', body: formData, credentials: 'include' }
      );

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const result = await res.json();

      if (result.status === true) {
        fetchStandardHours();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSavingStandard(false);
    }
  };

  // ── CUSTOM HANDLERS ───────────────────────────────────────────────────────
  const handleOpenMenu = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const openAddNew = () => {
    setIsEditMode(false);
    setCurrentId(null);
    setName('');
    setContent('');
    setFormError('');
    setOpenFormDialog(true);
  };

  const openEdit = (item) => {
    setIsEditMode(true);
    setCurrentId(item.id);
    setName(item.name || '');
    setContent(item.content || '');
    setFormError('');
    setOpenFormDialog(true);
    handleCloseMenu();
  };

  const handleFormSubmit = async () => {
    if (!name.trim()) return setFormError('Name is required');
    if (!content.trim()) return setFormError('Content is required');

    setSubmittingCustom(true);
    setFormError('');

    try {
      const formData = new FormData();
      formData.append('issetAddEditCustomOpeningHours', '1');
      formData.append('name', name.trim());
      formData.append('content', content.trim());
      if (isEditMode && currentId) formData.append('openingHours_id', currentId);

      const res = await fetch(
        `${config.reactApi_baseUrl}/react-api/opening-hours/add-edit-custom-opening-hours.php`,
        { method: 'POST', body: formData, credentials: 'include' }
      );

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const result = await res.json();

      if (result.status === true) {
        setOpenFormDialog(false);
        fetchCustomHours();
      } else {
        setFormError(getFirstErrorMessage(result.errorMsg) || 'Save failed');
      }
    } catch (err) {
      setFormError(err.message || 'Network error');
    } finally {
      setSubmittingCustom(false);
    }
  };

  const confirmDelete = (id) => {
    setDeleteId(id);
    setDeleteError('');
    setOpenDeleteDialog(true);
    handleCloseMenu();
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteId) return;
    setDeleteSubmitting(true);
    setDeleteError('');

    try {
      const formData = new FormData();
      formData.append('issetDeleteItem', '1');
      formData.append('openingHours_id', deleteId);

      const res = await fetch(
        `${config.reactApi_baseUrl}/react-api/opening-hours/delete-custom-opening-hours.php`,
        { method: 'POST', body: formData, credentials: 'include' }
      );

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const result = await res.json();

      if (result.status === true) {
        setOpenDeleteDialog(false);
        fetchCustomHours();
      } else {
        setDeleteError(getFirstErrorMessage(result.errorMsg) || 'Delete failed');
      }
    } catch (err) {
      setDeleteError(err.message || 'Network error');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  // ── EFFECTS ────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchStandardHours();
    fetchCustomHours();
  }, []);

  // ── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ px: { xs: 0, md: 0 } }}>
      <Typography
        variant="h4"
        fontWeight={800}
        mb={3}
        sx={{ textAlign: 'left' }}
      >
        OPENING HOURS
      </Typography>

      <Grid container spacing={3}>
        {/* Standard Opening Hours */}
        <Grid item  size={{md:6}}>
          <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
            <Typography variant="h5" fontWeight={800} sx={{ color: '#ff2c6d', mb: 1 }}>
              Standard opening hours
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {loadingStandard ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : errorStandard ? (
              <Alert severity="error">{errorStandard}</Alert>
            ) : (
              <>
                {days.map((day) => {
                  const h = standardHours[day] || {
                    opensAt: '00:00',
                    closesAt: '00:00',
                    isClosed: false,
                  };

                  return (
                    <Box key={day} sx={{ mb: 2 }}>
                      <Paper
                        variant="outlined"
                        sx={{
                          py: 0,
                          px: { xs: 1.5, sm: 2 },
                          borderRadius: 2,
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          justifyContent: 'space-between',
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          gap: { xs: 1.5, sm: 2 },
                          cursor: 'pointer',
                          backgroundColor: expandedDay === day ? '#f8f9ff' : 'white',
                        }}
                        onClick={() => !h.isClosed && toggleExpanded(day)}
                      >
                        <Typography fontWeight={700}>
                          {day}
                        </Typography>

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2.5,
                            flexWrap: 'wrap',
                            justifyContent: { xs: 'space-between', sm: 'flex-end' },
                            width: { xs: '100%', sm: 'auto' },
                          }}
                        >
                          {!h.isClosed && (
                            <Typography color="text.primary" sx={{ order: { xs: 1, sm: 0 } }}>
                              {h.opensAt} – {h.closesAt}
                            </Typography>
                          )}

                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={h.isClosed}
                                onChange={(e) => handleClosedChange(day, e.target.checked)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            }
                            label="Closed"
                            labelPlacement="end"
                            sx={{ marginRight: 0, order: { xs: 2, sm: 1 } }}
                          />
                        </Box>
                      </Paper>

                      <Collapse in={expandedDay === day} timeout="auto" unmountOnExit>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 2,
                            mt: 2,
                            px: { xs: 1, sm: 2 },
                            flexDirection: { xs: 'column', sm: 'row' },
                          }}
                        >
                          <FormControl fullWidth size="small">
                            <InputLabel id={`opens-at-label-${day}`}>Opens at</InputLabel>
                            <Select
                              labelId={`opens-at-label-${day}`}
                              value={h.opensAt}
                              label="Opens at"
                              onChange={(e) => handleTimeChange(day, 'opensAt', e.target.value)}
                              disabled={h.isClosed}
                            >
                              {timeOptions.map((time) => (
                                <SelectMenuItem key={time} value={time}>
                                  {time}
                                </SelectMenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          <FormControl fullWidth size="small">
                            <InputLabel id={`closes-at-label-${day}`}>Closes at</InputLabel>
                            <Select
                              labelId={`closes-at-label-${day}`}
                              value={h.closesAt}
                              label="Closes at"
                              onChange={(e) => handleTimeChange(day, 'closesAt', e.target.value)}
                              disabled={h.isClosed}
                            >
                              {timeOptions.map((time) => (
                                <SelectMenuItem key={time} value={time}>
                                  {time}
                                </SelectMenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}

                <Box sx={{ textAlign: 'right', mt: 3 }}>
                  <Button
                    variant="contained"
                    disabled={savingStandard}
                    onClick={handleSaveStandard}
                    sx={{
                      backgroundColor: '#1a2bdc',
                      px: { xs: 3, sm: 4 },
                      borderRadius: 2,
                      '&:hover': { backgroundColor: '#1320a8' },
                    }}
                  >
                    {savingStandard ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        </Grid>

        {/* Custom Opening Hours */}
        <Grid item size={{md:6}}>
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Typography variant="h5" fontWeight={800}>
                Custom Opening Hours
              </Typography>

              <Button
                variant="contained"
                onClick={openAddNew}
                sx={{
                  backgroundColor: '#ff2c6d',
                  borderRadius: 2,
                  fontWeight: 700,
                  '&:hover': { backgroundColor: '#e02660' },
                }}
              >
                + Add
              </Button>
            </Box>

            {loadingCustom ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : errorCustom ? (
              <Alert severity="error">{errorCustom}</Alert>
            ) : customHours.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                <Typography color="text.secondary">
                  No custom opening hours added yet
                </Typography>
              </Paper>
            ) : (
              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customHours.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.name}</TableCell>
                        <TableCell sx={{ whiteSpace: 'pre-line' }}>
                          {row.content}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={(e) => handleOpenMenu(e, row)}>
                            <img src={iconAngleDown} width={16} alt="action" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Custom Action Menu */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
              PaperProps={{ sx: { borderRadius: 2, width: 140 } }}
            >
              <MenuItem onClick={() => openEdit(selectedItem)}>Edit</MenuItem>
              <MenuItem onClick={() => confirmDelete(selectedItem?.id)} sx={{ color: 'error.main' }}>
                Delete
              </MenuItem>
            </Menu>

            {/* Delete Confirmation Dialog */}
            <Dialog
              open={openDeleteDialog}
              onClose={() => !deleteSubmitting && setOpenDeleteDialog(false)}
            >
              <DialogTitle>Confirm Delete</DialogTitle>
              <DialogContent>
                {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
                <DialogContentText>
                  Are you sure you want to delete this custom opening hours entry?<br />
                  This action cannot be undone.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenDeleteDialog(false)} disabled={deleteSubmitting}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDeleteConfirmed}
                  disabled={deleteSubmitting}
                >
                  {deleteSubmitting ? <CircularProgress size={24} /> : 'Delete'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Add / Edit Dialog */}
            <Dialog
              open={openFormDialog}
              onClose={() => !submittingCustom && setOpenFormDialog(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>{isEditMode ? 'Edit Custom Hours' : 'Add Custom Hours'}</DialogTitle>
              <DialogContent dividers>
                {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

                <TextField
                  autoFocus
                  margin="dense"
                  label="Name / Title"
                  fullWidth
                  variant="outlined"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={submittingCustom}
                  error={!!formError && !name.trim()}
                  helperText={!name.trim() && formError ? 'Required' : ''}
                  sx={{ mb: 2 }}
                />

                <TextField
                  margin="dense"
                  label="Content (e.g. Friday to Sunday...)"
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  disabled={submittingCustom}
                  error={!!formError && !content.trim()}
                  helperText={!content.trim() && formError ? 'Required' : ''}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenFormDialog(false)} disabled={submittingCustom}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: '#ff2c6d',
                    '&:hover': { backgroundColor: '#e02660' },
                  }}
                  onClick={handleFormSubmit}
                  disabled={submittingCustom || !name.trim() || !content.trim()}
                >
                  {submittingCustom ? <CircularProgress size={24} /> : isEditMode ? 'Update' : 'Save'}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}